// The active homebrew STACK, held at module scope beside the active system for
// the same reason that one is (config/system.js): the axios request interceptor
// cannot read React state, and neither can `useListNav`'s module-level cache.
//
// MRCS Phase B built the read path; Phase D, slice D4 gave it a door — a layer
// switched on in the homebrew editor is a `stack_config` the server reports
// back, and this is where the browser keeps it. With nothing switched on this
// module holds NOTHING and every function below returns the value that makes
// its caller behave exactly as it did before the file existed, which is the
// invariant (09 §2): with no stack active, every response and every rendered
// page is identical to the pre-layer engine.
//
// ── ONE STACK PER SYSTEM, NOT ONE STACK ──
//
// A layer belongs to exactly one system (09 §3 decision 3) and so does a stack
// (`stack_config.system_id`), so "the active stack" is only a well-formed idea
// once you say which system's. D4 found the alternative: with a single slot,
// switching a homebrew on for Pathfinder silently discards the one switched on
// for 3.5, and switching systems back leaves it off with nothing in the UI to
// explain why. The server would have ignored the mismatched id anyway —
// `stackMiddleware` requires `stack.systemId === req.systemId` — so a single
// slot could never have been more than one system's worth of memory.
//
// ── THE SIGNATURE, and why it is stored beside the id ──
//
// 01 §5 fixes the stack-signature format —
//
//     <system-slug> "@v" <content_version>
//       { "+" <layer_id> ":" ( <layer_version_id> | "~" <draft_revision> ) }*
//
// — and 05 §4 records that `useListNav` must key on it, or a filtered list
// saved under one stack restores under another. The signature is SERVER
// knowledge: it names the system's content version and what each layer resolves
// against, neither of which the client can compute. So the client stores
// whatever the server last told it and falls back to the stack id, which is the
// whole of what it knows until an activation reports the rest. Both are stable
// per stack, so the cache key is correct either way; the signature additionally
// moves when the CONTENT does, which is what makes an edit visible to a cache
// rather than only to a page load.

import { getActiveSystem } from './system';

const KEY = 'contentStacks';

const readAll = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {}; // localStorage unavailable (private mode / SSR), or corrupt
  }
};

let stacks = readAll();

const current = () => stacks[getActiveSystem()] || null;

/** The stack id to send as `?stack=`, or null when none is active here. */
export const getActiveStack = () => {
  const hit = current();
  return hit ? hit.id : null;
};

/** The whole record for the active system, for a UI that wants to name it. */
export const getActiveStackRecord = () => current();

/**
 * The cache-key suffix every per-system client cache appends.
 *
 * `''` when no stack is active — so a key built with it is byte-identical to
 * the key built without it, which is what keeps every existing cached state
 * readable and every existing test true.
 */
export const getStackKey = () => {
  const hit = current();
  if (!hit) return '';
  return `::${hit.signature || `stack-${hit.id}`}`;
};

/**
 * Remember (or forget) the stack for one system.
 *
 * `system` defaults to the active one, which is the case every caller in the
 * app is in — the editor activates a layer while looking at it. It is a
 * parameter at all because the server's reply names the system the layer
 * belongs to, and that is more trustworthy than whatever the header happens to
 * be showing.
 */
export const setActiveStack = (stack, system = getActiveSystem()) => {
  const id = stack && (stack.id != null ? String(stack.id) : null);
  const next = { ...stacks };
  // `name` is what a page says it is reading under (a party's table, since
  // Parties C5); absent for a person's own homebrew config.
  if (id) next[system] = { id, signature: (stack && stack.signature) || null, ...(stack && stack.name ? { name: stack.name } : {}) };
  else delete next[system];
  stacks = next;
  try {
    if (Object.keys(stacks).length) localStorage.setItem(KEY, JSON.stringify(stacks));
    else localStorage.removeItem(KEY);
  } catch (e) {
    /* localStorage unavailable — keep the in-memory value */
  }
};
